import { canonicalJson, sha256Hex } from '../../reportGeneration/reportContractHash';
import {
  EO_KPI_UNCERTAINTY_DIGEST_ALGORITHM,
  EO_KPI_UNCERTAINTY_KPI_IDS,
  EoKpiUncertaintyDefinitionContractError,
  parseEoKpiUncertaintyDefinitionV1,
  type EoKpiUncertaintyDefinitionErrorCode,
  type EoKpiUncertaintyDefinitionV1,
  type EoKpiUncertaintyKpiId,
} from './eoKpiUncertaintyDefinition';

/** Portable revision-owned collection of complete per-KPI uncertainty definitions. */
export const EO_KPI_UNCERTAINTY_DEFINITION_SET_SCHEMA_VERSION = 1 as const;
export const EO_KPI_UNCERTAINTY_DEFINITION_SET_CONTRACT_ID =
  'eo-kpi-uncertainty-definition-set-v1' as const;
export const EO_KPI_UNCERTAINTY_DEFINITION_SET_DIGEST_ALGORITHM =
  EO_KPI_UNCERTAINTY_DIGEST_ALGORITHM;
/** Leaves headroom inside the 650 kB immutable simulation-revision contract. */
export const EO_KPI_UNCERTAINTY_DEFINITION_SET_MAX_BYTES = 400_000 as const;

export interface EoKpiUncertaintyDefinitionSetBuildInputV1 {
  definitions: ReadonlyArray<EoKpiUncertaintyDefinitionV1>;
}

export interface EoKpiUncertaintyDefinitionSetV1 {
  schemaVersion: 1;
  contractId: typeof EO_KPI_UNCERTAINTY_DEFINITION_SET_CONTRACT_ID;
  status: 'authored';
  /** Redundant, Firestore-safe canonical order projection. */
  kpiIds: ReadonlyArray<EoKpiUncertaintyKpiId>;
  /**
   * Canonical JSON for each exact core definition. Firestore rejects arrays
   * directly containing arrays, so the embedded 6x6 matrix must cross the
   * revision persistence boundary as verified JSON bytes rather than an object.
   */
  definitionJson: ReadonlyArray<string>;
  digestAlgorithm: typeof EO_KPI_UNCERTAINTY_DEFINITION_SET_DIGEST_ALGORITHM;
  definitionSetDigest: string;
}

export type EoKpiUncertaintyDefinitionSetErrorCode =
  | 'EO_UNCERTAINTY_DEFINITION_SET_INVALID_SHAPE'
  | 'EO_UNCERTAINTY_DEFINITION_SET_INVALID_COUNT'
  | 'EO_UNCERTAINTY_DEFINITION_SET_DUPLICATE_KPI'
  | 'EO_UNCERTAINTY_DEFINITION_SET_DUPLICATE_DEFINITION'
  | 'EO_UNCERTAINTY_DEFINITION_SET_NONCANONICAL_ORDER'
  | 'EO_UNCERTAINTY_DEFINITION_SET_DIGEST_MISMATCH'
  | 'EO_UNCERTAINTY_DEFINITION_SET_TOO_LARGE';

export type EoKpiUncertaintyDefinitionSetVerificationErrorCode =
  EoKpiUncertaintyDefinitionSetErrorCode | EoKpiUncertaintyDefinitionErrorCode;

export class EoKpiUncertaintyDefinitionSetContractError extends TypeError {
  readonly code: EoKpiUncertaintyDefinitionSetErrorCode;
  readonly field: string;

  constructor(
    code: EoKpiUncertaintyDefinitionSetErrorCode,
    message: string,
    field: string,
  ) {
    super(`${code}: ${message} (${field})`);
    this.name = 'EoKpiUncertaintyDefinitionSetContractError';
    this.code = code;
    this.field = field;
  }
}

export type EoKpiUncertaintyDefinitionSetVerificationV1 =
  | { valid: true; definitionSet: EoKpiUncertaintyDefinitionSetV1 }
  | {
    valid: false;
    error: Readonly<{
      code: EoKpiUncertaintyDefinitionSetVerificationErrorCode;
      field: string;
      message: string;
    }>;
  };

type UnknownRecord = Record<string, unknown>;
type DefinitionSetDigestBodyV1 = Omit<
  EoKpiUncertaintyDefinitionSetV1,
  'definitionSetDigest'
>;

const SHA256 = /^[a-f0-9]{64}$/u;

function fail(
  code: EoKpiUncertaintyDefinitionSetErrorCode,
  message: string,
  field: string,
): never {
  throw new EoKpiUncertaintyDefinitionSetContractError(code, message, field);
}

function record(value: unknown, field: string): UnknownRecord {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    fail('EO_UNCERTAINTY_DEFINITION_SET_INVALID_SHAPE', 'Expected an object.', field);
  }
  return value as UnknownRecord;
}

function exactKeys(value: UnknownRecord, expected: readonly string[], field: string): void {
  const keys = Object.keys(value).sort();
  const wanted = [...expected].sort();
  if (keys.length !== wanted.length || keys.some((key, index) => key !== wanted[index])) {
    fail(
      'EO_UNCERTAINTY_DEFINITION_SET_INVALID_SHAPE',
      'Object keys do not match the definition-set contract.',
      field,
    );
  }
}

function assertBoundedCanonicalJson(value: unknown, field: string): void {
  let serialized: string;
  try {
    serialized = canonicalJson(value);
  } catch {
    fail(
      'EO_UNCERTAINTY_DEFINITION_SET_INVALID_SHAPE',
      'Value is not finite acyclic JSON.',
      field,
    );
  }
  if (new TextEncoder().encode(serialized).byteLength
      > EO_KPI_UNCERTAINTY_DEFINITION_SET_MAX_BYTES) {
    fail(
      'EO_UNCERTAINTY_DEFINITION_SET_TOO_LARGE',
      `Definition set exceeds ${EO_KPI_UNCERTAINTY_DEFINITION_SET_MAX_BYTES} canonical JSON bytes.`,
      field,
    );
  }
}

function parseDefinitionObjects(value: unknown): ReadonlyArray<EoKpiUncertaintyDefinitionV1> {
  if (!Array.isArray(value)
      || value.length < 1
      || value.length > EO_KPI_UNCERTAINTY_KPI_IDS.length) {
    fail(
      'EO_UNCERTAINTY_DEFINITION_SET_INVALID_COUNT',
      `Expected 1-${EO_KPI_UNCERTAINTY_KPI_IDS.length} definitions.`,
      'definitionSetInput.definitions',
    );
  }

  const definitions = value.map((entry, index) => {
    try {
      return parseEoKpiUncertaintyDefinitionV1(entry);
    } catch (error) {
      if (error instanceof EoKpiUncertaintyDefinitionContractError) throw error;
      fail(
        'EO_UNCERTAINTY_DEFINITION_SET_INVALID_SHAPE',
        'Nested definition could not be parsed.',
        `definitionSetInput.definitions[${index}]`,
      );
    }
  });

  const seenKpis = new Set<EoKpiUncertaintyKpiId>();
  const seenDefinitionIds = new Set<string>();
  for (let index = 0; index < definitions.length; index += 1) {
    const definition = definitions[index]!;
    if (seenKpis.has(definition.kpiId)) {
      fail(
        'EO_UNCERTAINTY_DEFINITION_SET_DUPLICATE_KPI',
        `KPI ${definition.kpiId} occurs more than once.`,
        `definitionSetInput.definitions[${index}].kpiId`,
      );
    }
    if (seenDefinitionIds.has(definition.definitionId)) {
      fail(
        'EO_UNCERTAINTY_DEFINITION_SET_DUPLICATE_DEFINITION',
        `Definition id ${definition.definitionId} occurs more than once.`,
        `definitionSetInput.definitions[${index}].definitionId`,
      );
    }
    seenKpis.add(definition.kpiId);
    seenDefinitionIds.add(definition.definitionId);

    const currentOrder = EO_KPI_UNCERTAINTY_KPI_IDS.indexOf(definition.kpiId);
    const previous = definitions[index - 1];
    if (previous !== undefined
        && EO_KPI_UNCERTAINTY_KPI_IDS.indexOf(previous.kpiId) >= currentOrder) {
      fail(
        'EO_UNCERTAINTY_DEFINITION_SET_NONCANONICAL_ORDER',
        'Definitions must follow the canonical EO KPI order.',
        `definitionSetInput.definitions[${index}].kpiId`,
      );
    }
  }
  return Object.freeze(definitions);
}

function parseDefinitionJson(value: unknown): ReadonlyArray<EoKpiUncertaintyDefinitionV1> {
  if (!Array.isArray(value)
      || value.length < 1
      || value.length > EO_KPI_UNCERTAINTY_KPI_IDS.length
      || value.some(entry => typeof entry !== 'string')) {
    fail(
      'EO_UNCERTAINTY_DEFINITION_SET_INVALID_COUNT',
      `Expected 1-${EO_KPI_UNCERTAINTY_KPI_IDS.length} canonical definition JSON strings.`,
      'definitionSet.definitionJson',
    );
  }
  const decoded = value.map((entry, index) => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(entry as string);
    } catch {
      fail(
        'EO_UNCERTAINTY_DEFINITION_SET_INVALID_SHAPE',
        'Definition JSON is invalid.',
        `definitionSet.definitionJson[${index}]`,
      );
    }
    const definition = parseEoKpiUncertaintyDefinitionV1(parsed);
    if (canonicalJson(definition) !== entry) {
      fail(
        'EO_UNCERTAINTY_DEFINITION_SET_INVALID_SHAPE',
        'Definition JSON must use the exact canonical encoding.',
        `definitionSet.definitionJson[${index}]`,
      );
    }
    return definition;
  });
  // Reuse all uniqueness and order checks without accepting rewritten bytes.
  return parseDefinitionObjects(decoded);
}

function definitionSetBody(
  kpiIds: ReadonlyArray<EoKpiUncertaintyKpiId>,
  definitionJson: ReadonlyArray<string>,
): DefinitionSetDigestBodyV1 {
  return {
    schemaVersion: EO_KPI_UNCERTAINTY_DEFINITION_SET_SCHEMA_VERSION,
    contractId: EO_KPI_UNCERTAINTY_DEFINITION_SET_CONTRACT_ID,
    status: 'authored',
    kpiIds,
    definitionJson,
    digestAlgorithm: EO_KPI_UNCERTAINTY_DEFINITION_SET_DIGEST_ALGORITHM,
  };
}

/** Digest every set field, including every exact nested definition, except the digest itself. */
export function eoKpiUncertaintyDefinitionSetDigestV1(
  body: DefinitionSetDigestBodyV1,
): string {
  return sha256Hex(canonicalJson(body));
}

function freezeDefinitionSet(
  body: DefinitionSetDigestBodyV1,
  definitionSetDigest: string,
): EoKpiUncertaintyDefinitionSetV1 {
  return Object.freeze({ ...body, definitionSetDigest });
}

/** Build a portable revision configuration from already-complete authored definitions. */
export function buildEoKpiUncertaintyDefinitionSetV1(
  input: EoKpiUncertaintyDefinitionSetBuildInputV1,
): EoKpiUncertaintyDefinitionSetV1 {
  assertBoundedCanonicalJson(input, 'definitionSetInput');
  const source = record(input, 'definitionSetInput');
  exactKeys(source, ['definitions'], 'definitionSetInput');
  const definitions = parseDefinitionObjects(source.definitions);
  const body = definitionSetBody(
    Object.freeze(definitions.map(definition => definition.kpiId)),
    Object.freeze(definitions.map(definition => canonicalJson(definition))),
  );
  const result = freezeDefinitionSet(body, eoKpiUncertaintyDefinitionSetDigestV1(body));
  assertBoundedCanonicalJson(result, 'definitionSet');
  return result;
}

/** Parse persisted revision configuration and independently verify nested and set digests. */
export function parseEoKpiUncertaintyDefinitionSetV1(
  value: unknown,
): EoKpiUncertaintyDefinitionSetV1 {
  assertBoundedCanonicalJson(value, 'definitionSet');
  const source = record(value, 'definitionSet');
  exactKeys(source, [
    'schemaVersion',
    'contractId',
    'status',
    'kpiIds',
    'definitionJson',
    'digestAlgorithm',
    'definitionSetDigest',
  ], 'definitionSet');
  if (source.schemaVersion !== EO_KPI_UNCERTAINTY_DEFINITION_SET_SCHEMA_VERSION
      || source.contractId !== EO_KPI_UNCERTAINTY_DEFINITION_SET_CONTRACT_ID
      || source.status !== 'authored'
      || source.digestAlgorithm !== EO_KPI_UNCERTAINTY_DEFINITION_SET_DIGEST_ALGORITHM) {
    fail(
      'EO_UNCERTAINTY_DEFINITION_SET_INVALID_SHAPE',
      'Definition-set constants do not match v1.',
      'definitionSet',
    );
  }
  const definitions = parseDefinitionJson(source.definitionJson);
  const kpiIds = Object.freeze(definitions.map(definition => definition.kpiId));
  if (!Array.isArray(source.kpiIds)
      || canonicalJson(source.kpiIds) !== canonicalJson(kpiIds)) {
    fail(
      'EO_UNCERTAINTY_DEFINITION_SET_NONCANONICAL_ORDER',
      'KPI ids must exactly match the canonical nested definition order.',
      'definitionSet.kpiIds',
    );
  }
  const body = definitionSetBody(
    kpiIds,
    Object.freeze([...(source.definitionJson as string[])]),
  );
  if (typeof source.definitionSetDigest !== 'string'
      || !SHA256.test(source.definitionSetDigest)) {
    fail(
      'EO_UNCERTAINTY_DEFINITION_SET_DIGEST_MISMATCH',
      'Definition-set digest is not lowercase SHA-256.',
      'definitionSet.definitionSetDigest',
    );
  }
  const expectedDigest = eoKpiUncertaintyDefinitionSetDigestV1(body);
  if (source.definitionSetDigest !== expectedDigest) {
    fail(
      'EO_UNCERTAINTY_DEFINITION_SET_DIGEST_MISMATCH',
      'Definition-set digest does not match its canonical payload.',
      'definitionSet.definitionSetDigest',
    );
  }
  return freezeDefinitionSet(body, expectedDigest);
}

/** Decode and reverify every exact core definition from a valid Firestore-safe set. */
export function eoKpiUncertaintyDefinitionsFromSetV1(
  value: EoKpiUncertaintyDefinitionSetV1,
): ReadonlyArray<EoKpiUncertaintyDefinitionV1> {
  const parsed = parseEoKpiUncertaintyDefinitionSetV1(value);
  return parseDefinitionJson(parsed.definitionJson);
}

/** Non-throwing trust-boundary helper for portable revision configurations. */
export function verifyEoKpiUncertaintyDefinitionSetV1(
  value: unknown,
): EoKpiUncertaintyDefinitionSetVerificationV1 {
  try {
    return { valid: true, definitionSet: parseEoKpiUncertaintyDefinitionSetV1(value) };
  } catch (error) {
    if (error instanceof EoKpiUncertaintyDefinitionSetContractError
        || error instanceof EoKpiUncertaintyDefinitionContractError) {
      return {
        valid: false,
        error: Object.freeze({ code: error.code, field: error.field, message: error.message }),
      };
    }
    return {
      valid: false,
      error: Object.freeze({
        code: 'EO_UNCERTAINTY_DEFINITION_SET_INVALID_SHAPE',
        field: 'definitionSet',
        message: error instanceof Error ? error.message : 'Unknown definition-set error.',
      }),
    };
  }
}
